import type { GetStaticPaths, GetStaticProps, NextPage } from 'next'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import {
  ArticleJsonLd,
  BreadcrumbJsonLd,
  FAQPageJsonLd,
  HowToJsonLd,
  NextSeo
} from 'next-seo'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import { default as Blog } from 'src/components/Blog'
import MoreBlogs from 'src/components/Blog/MoreBlogs'
import Footer from 'src/components/Footer'
import RelatedArticles from 'src/components/Help/RelatedArticles'
import NavBar from 'src/components/LandingNav'
import { PILLAR_PAGE_MAP, PillarPage } from 'src/constants/searchPages'
import { DEFAULT_STRUCTURED_DATA } from 'src/constants/structuredData'
import RecommendedSection from 'src/screens/Landing/RecommendedSection'
import { IPost, PostOrderByInput } from 'src/types/hygraph'
import { RedirectUrls } from 'src/types/types'
import {
  formatTimestampMonthDayYear,
  formatUtcDateMonthDayYear
} from 'src/utils'
import {
  IBlogPillar,
  PILLAR_BLOG_TOPICS,
  TOPIC_MAP,
  getPost,
  getPostTopic,
  getPosts
} from 'src/utils/hygraph'
import loggerServer from 'src/utils/loggerServer'
import {
  HYGRAPH_PAGE_REVALIDATE_SECONDS,
  getLocalePrefix,
  getPostAuthor,
  getPostDates,
  withCleanTitle
} from 'src/utils/postSeo'
import { createSEODescriptions, toOgLocale } from 'src/utils/seo'
const WhatsAppButton = dynamic(() => import('src/components/WhatsappButton'))

// Admin-only client component — render purely on the client to avoid the
// React 19 streaming-Suspense hydration mismatch (#418) we hit in
// LandingNav. The button returns null for non-admins anyway, so SSRing it
// has no SEO/UX value.
const RevalidateButton = dynamic(
  () => import('src/components/BlogManager/RevalidateButton'),
  { ssr: false }
)

const image = 'https://www.spotme.mx/images/cover.png'
const SITE_URL = 'https://www.spotme.mx'

// Mirrors the visible breadcrumb in src/components/Blog.
const getPillarLabel = (pillar: IBlogPillar | undefined): string => {
  if (!pillar) return 'SpotMe'
  if (pillar === 'anfitrion') return 'Anfitrión'
  return PILLAR_PAGE_MAP[pillar].label
}

type IProps = {
  post: IPost
  // <title> fallback keeps the raw CMS title (no-title-churn rule); only the
  // H1 / headline / breadcrumb use the brand-stripped `post.title`.
  metaTitle: string
  morePosts: IPost[]
  locale: string
  pillar: PillarPage | null
  optimizedDescription: string
  optimizedDescriptionOg: string
  formattedDate: string
}

const Page: NextPage<IProps> = ({
  post,
  metaTitle,
  morePosts,
  locale,
  pillar,
  optimizedDescription,
  optimizedDescriptionOg,
  formattedDate
}) => {
  const { t } = useTranslation('blog')
  const { t: tCommon } = useTranslation('common')
  const { title, slug, structuredData, coverImage } = post
  const { topic, basePath } = getPostTopic(post)
  const url = `${SITE_URL}${basePath}/${slug}`
  const { datePublished, dateModified } = getPostDates(post)
  const ogImage = coverImage?.url ?? image
  const sd = Array.isArray(structuredData) ? structuredData : []
  const FAQs = sd.find(({ type }: { type: string }) => type === 'FAQ') as
    | { type: 'FAQ'; items: { title: string; description: string }[] }
    | undefined
  const HowTo = sd.find(({ type }: { type: string }) => type === 'HowTo') as
    | {
        type: 'HowTo'
        name?: string
        totalTime?: string
        steps: { name: string; text: string; url?: string }[]
      }
    | undefined

  return (
    <>
      <NextSeo
        title={metaTitle}
        description={optimizedDescription}
        canonical={url}
        openGraph={{
          type: 'article',
          url,
          title: metaTitle,
          description: optimizedDescriptionOg,
          locale: toOgLocale(locale),
          images: [{ url: ogImage, width: 1200, height: 630 }],
          article: {
            publishedTime: datePublished,
            modifiedTime: dateModified
          }
        }}
      />
      <ArticleJsonLd
        type="BlogPosting"
        url={url}
        title={title}
        images={[ogImage]}
        datePublished={datePublished}
        dateModified={dateModified}
        authorName={getPostAuthor(post)}
        description={optimizedDescription}
        isAccessibleForFree
        publisherLogo={DEFAULT_STRUCTURED_DATA.logo}
        publisherName={DEFAULT_STRUCTURED_DATA.name}
      />
      {topic && basePath && (
        <BreadcrumbJsonLd
          itemListElements={[
            {
              position: 1,
              name: t('ARTICLE.BREADCRUMB_HOME'),
              item: SITE_URL
            },
            {
              position: 2,
              name: getPillarLabel(topic.pillar),
              item: `${SITE_URL}/${topic.pillar}`
            },
            {
              position: 3,
              name: t('ARTICLE.BREADCRUMB_BLOG'),
              item: `${SITE_URL}${basePath}`
            },
            { position: 4, name: title, item: url }
          ]}
        />
      )}
      {FAQs && (
        <FAQPageJsonLd
          mainEntity={FAQs.items.map(({ title, description }) => ({
            questionName: title,
            acceptedAnswerText: description
          }))}
        />
      )}
      {HowTo && HowTo.steps?.length > 0 && (
        <HowToJsonLd
          name={HowTo.name ?? title}
          image={ogImage}
          totalTime={HowTo.totalTime}
          step={HowTo.steps.map((s) => ({
            name: s.name,
            text: s.text,
            url: s.url
          }))}
        />
      )}

      <main className="bg-white-500">
        <NavBar />

        <div className="mx-auto w-full max-w-7xl px-5 md:px-10">
          <div className="flex flex-col gap-12 lg:flex-row lg:gap-16">
            {/* Main article column */}
            <div className="min-w-0 flex-1">
              <Blog post={post} formattedDate={formattedDate} />
              <RelatedArticles
                post={post}
                variant="blog"
                className="mt-16 md:mt-20"
              />
            </div>

            {/* Sidebar — desktop only */}
            <div className="hidden pt-36 lg:block lg:w-[300px] lg:shrink-0">
              <MoreBlogs morePosts={morePosts} />
            </div>
          </div>

          {/* Editorial CTA — 12-col rail, hairline */}
          <section className="mt-20 border-t border-ink-100/15 pt-14 md:mt-28 md:pt-20">
            <div className="md:grid md:grid-cols-12 md:gap-12">
              <div className="md:col-span-7">
                <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
                  {t('CTA.EYEBROW')}
                </p>
                <h2 className="mt-4 max-w-xl font-display text-[28px] leading-[1.12] font-medium tracking-[-0.01em] text-ink-700 md:mt-5 md:text-[44px]">
                  {t('CTA.TITLE_DARK')}{' '}
                  <span className="font-display font-normal text-blue-500 italic">
                    {t('CTA.TITLE_BLUE')}
                  </span>
                </h2>
              </div>
              <div className="mt-6 flex flex-col gap-5 md:col-span-5 md:mt-0 md:items-end md:self-end">
                <p className="max-w-sm text-[14px] leading-relaxed text-ink-200 md:text-right md:text-[15px]">
                  {t('CTA.DESC')}
                </p>
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-4">
                  <Link
                    href={RedirectUrls.Search}
                    className="rounded-full bg-blue-500 px-6 py-3 text-center text-[14px] font-semibold text-white-500 shadow-[0_18px_40px_-18px_rgba(0,164,255,0.55)] transition-all duration-200 hover:bg-blue-600 active:scale-[0.98]"
                  >
                    {t('CTA.PRIMARY')}
                  </Link>
                  <Link
                    href={RedirectUrls.Search}
                    className="inline-flex items-center justify-center text-[14px] font-medium text-ink-700 underline decoration-blue-500/40 decoration-1 underline-offset-[6px] transition-colors hover:text-blue-500 hover:decoration-blue-500"
                  >
                    {t('CTA.SECONDARY')}
                  </Link>
                </div>
              </div>
            </div>
          </section>

          {/* More articles — mobile only */}
          <div className="mt-16 lg:hidden">
            <MoreBlogs morePosts={morePosts} />
          </div>

          {/* Recommendations */}
          <div className="mt-20 md:mt-28">
            <RecommendedSection
              surface="blog_recommended"
              pillar={pillar ?? undefined}
            />
          </div>
        </div>

        <RevalidateButton />

        <Footer />
        <WhatsAppButton
          msg={tCommon('HELP.ARTICLE_WHATSAPP_MSG', { title: post.title })}
        />
      </main>
    </>
  )
}

export default Page

type IParams = { pillar: IBlogPillar; slug: string }

export const getStaticPaths = (async ({ locales = [] }) => {
  const paths: { params: IParams; locale: string }[] = []

  for (const topic of PILLAR_BLOG_TOPICS) {
    const res = await getPosts(1000, 0, PostOrderByInput.PublishedAtDesc, {
      topic,
      listOnly: true
    })
    const pillar = TOPIC_MAP[topic].pillar as IBlogPillar

    for (const locale of locales) {
      for (const { slug } of res.data?.posts ?? []) {
        paths.push({
          params: { slug: slug as string, pillar },
          locale
        })
      }
    }
  }

  return { paths, fallback: 'blocking' }
}) satisfies GetStaticPaths

const NOT_FOUND = {
  notFound: true,
  revalidate: HYGRAPH_PAGE_REVALIDATE_SECONDS
} as const

const getFormattedDate = (post: IPost): string =>
  post.date
    ? formatUtcDateMonthDayYear(post.date)
    : formatTimestampMonthDayYear(post.createdAt)

/**
 * `fixedPillar` is for routes without a `[pillar]` segment (`/anfitrion/blog/[slug]`):
 * their fallback renders receive only `{ slug }` in params.
 */
export const createGetStaticProps =
  (fixedPillar?: IBlogPillar): GetStaticProps<IProps> =>
  async (context) => {
    const { locale = 'es', params } = context
    const { slug, pillar: pillarParam } = params as IParams
    const blogPillar = fixedPillar ?? pillarParam

    // No module-level slug allow-list: it was cached for the life of the
    // server instance, so articles published after warm-up 404'd. getPost()
    // resolves from the 30-min helper cache, then Hygraph directly.
    const response = await getPost(slug, locale)
    const rawPost: IPost | null = response?.data?.post ?? null
    if (!rawPost) {
      loggerServer.warn('Error retrieving post from hygraph.', {
        slug,
        response
      })
      return NOT_FOUND
    }

    // Only blog topics carry a pillar → basePath; help articles live under
    // /acerca-de-spotme and must not render here.
    const { basePath } = getPostTopic(rawPost)
    if (!basePath) return NOT_FOUND

    // One canonical URL per post: any other pillar prefix 308s to it.
    if (basePath !== `/${blogPillar}/blog`) {
      return {
        redirect: {
          destination: `${getLocalePrefix(locale)}${basePath}/${slug}`,
          permanent: true
        },
        revalidate: HYGRAPH_PAGE_REVALIDATE_SECONDS
      }
    }

    const translations = await serverSideTranslations(locale ?? 'es', [
      'footer',
      'nav',
      'landing',
      'platform_stats',
      'common',
      'auth',
      'blog'
    ])

    const responsePosts = await getPosts(
      6,
      0,
      PostOrderByInput.PublishedAtDesc,
      { isBlog: true, locale, listOnly: true }
    )
    const morePosts = (responsePosts.data?.posts ?? [])
      .filter((p: { slug?: string | null }) => p.slug !== slug)
      .slice(0, 5)
      .map(withCleanTitle)

    const post = withCleanTitle(rawPost)
    const { title, description } = post
    // Optimize meta descriptions for SEO
    const {
      description: optimizedDescription,
      descriptionOg: optimizedDescriptionOg
    } = createSEODescriptions(
      description,
      `Lee sobre ${title} en el blog de SpotMe. Encuentra consejos útiles y información relevante.`
    )

    const pillar = Object.values(PillarPage).includes(blogPillar as PillarPage)
      ? (blogPillar as PillarPage)
      : null

    return {
      props: {
        post,
        metaTitle: rawPost.titleOg ?? rawPost.title,
        morePosts,
        locale,
        pillar,
        ...translations,
        optimizedDescription,
        optimizedDescriptionOg,
        formattedDate: getFormattedDate(post)
      },
      revalidate: HYGRAPH_PAGE_REVALIDATE_SECONDS
    }
  }

export const getStaticProps = createGetStaticProps()
